<template>
  <fts-design-modal
    :title="title"
    desc="columns designer"
    class="fts-listing-column-design-modal"
    ref="ftsDesignModal"
    @closed="reset"
  >
    <!-- body -->
    <template #body>
      <div class="list-column-filter fts-form-control-filter comfort-form-design">
        <input
          class="fts-form-control"
          v-model="filterText"
          placeholder="Search..."
        />
        <div class="search-icon">
          <font-awesome-icon :icon="['fas', 'search']" fixed-width />
        </div>
      </div>

      <div class="list-column-header">
        <checkbox
          color="var(--fts-primary)"
          :size="18"
          v-model="selectAll"
          class="list-column-checkbox"
        />
        <div>
          <span class="list-column-header-title">Columns</span> 
          <fts-badge color="text" variant="pill" size="sm" v-if="!!selectedCount">
            {{selectedCount}}
          </fts-badge>
        </div>
      </div>

      <div class="list-column-divider" />

      <div
        class="list-column"
        v-for="column in getColumnData"
        :key="column.id"
        @click="toggleSelect(column)"
      >
        <checkbox
          color="var(--fts-primary)"
          :size="18"
          v-model="column.selected"
          class="list-column-checkbox stopPropagation"
        />
        <div class="list-column-content">
          <div class="list-column-info">
            <i
              class="list-column-info__icon"
              :class="[
                informationIcon(column.type),
                {'list-column-info__icon--system': column.system}
              ]"
            ></i>
            <div class="list-column-info__name">
              {{column.displayName}}
            </div>
          </div>
        </div>
      </div>

      <div class="list-column-blank" v-if="filterText && !getColumnData.length">
        <div class="list-column-blank-title">No result found</div>
        <div class="list-column-blank-description">Try different search term</div>
      </div>
    </template>
    <!-- footer:left -->
    <template #footerLeft>
      <fts-btn
        text
        icon
        @click="remove"
        v-if="Boolean(columns && columns.length)" 
      >
        <font-awesome-icon :icon="['far', 'trash-alt']" />
      </fts-btn>
    </template>
    <!-- footer:right -->
    <template #footerRight>
      <!-- cancel -->
      <fts-btn
        text
        @click="close"
      >
        Cancel
      </fts-btn>
      <!-- done -->
      <fts-btn
        outline
        color="primary"
        @click="done"
      >
        <font-awesome-icon :icon="['fas', 'check']" /> Ok
      </fts-btn>
    </template>
  </fts-design-modal>
</template>

<script>
import commons from '../commons/commons';

import FTSBadge from '../elements/FTSBadge';
import FTSBtn from '../elements/FTSBtn';
import FTSDesignModal from './FTSDesignModal';

import Checkbox from 'vue-material-checkbox';

export default {
  components: {
    "fts-badge": FTSBadge,
    "fts-btn": FTSBtn,
    "fts-design-modal": FTSDesignModal,
    "checkbox": Checkbox,
  },
  props: {
    title: {
      type: String,
      default: 'Columns Configuration',
    },
    columns: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    filterText: null,
    columnData: [],
    resolve: null,
  }),
  methods: {
    async show(columnData) {
      this.columnData = _.cloneDeep(columnData);
      this.columnData.forEach(x => {
        this.$delete(x, "selected");
      });

      this.columns.forEach(x => {
        let selectedColumn = this.columnData.find(
          y => y.fieldName == x.fieldName && y.system == x.system         
        );
        if (selectedColumn)
          this.$set(selectedColumn, "selected", true);
      });

      return new Promise((resolve) => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show();
      });
    },
    done() {
      this.resolve && this.resolve(this.columnData.filter(x => x.selected));
      this.$refs.ftsDesignModal.close();
    },
    remove() {
      this.resolve && this.resolve(true);
      this.$refs.ftsDesignModal.close();
    },
    close() {
      this.$refs.ftsDesignModal.close();
    },
    reset() {
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    informationIcon(id) {
      let typeId = id;
      if (id === 'state' || id === 'collaboration')
        typeId = 7;

      return commons.informationIcon(typeId) || "fts-icon-text";
    },
    toggleSelect(item) {
      if (commons.elementStopPropagation()) return;      
      this.$set(item, 'selected', !Boolean(item.selected));
    },
  },
  computed: {
    getColumnData() {
      return this.filterText ?
        this.columnData.filter(x => 
          x.displayName.toLowerCase().indexOf(this.filterText.toLowerCase()) !== -1
        ) : 
        this.columnData;
    },
    selectAll: {
      get() {
        return this.columnData.every(x => x.selected);
      },
      set(val) {
        this.columnData.forEach(x => 
          this.$set(x, 'selected', val)
        );
      }
    },
    selectedCount() {
      const selectedCount = this.columnData.filter(x => x.selected).length;
      return selectedCount === this.columnData.length ? `all (${selectedCount})` : selectedCount;
    },
  },
}
</script>

<style lang="scss">
.list-column-filter {
  margin-bottom: 1rem;
}
.list-column-divider {
  margin-bottom: .375rem;
  border-bottom: 1px solid var(--fts-gray-200);
}
.list-column-header {
  display: flex;
  align-items: center;
  padding: .575rem 0;
  .list-column-header-title {
    margin-right: .375rem;
    font-weight: 500;
    line-height: 1.5;
  }
}
.list-column-checkbox {
  width: auto;
  margin: 0;
}
.list-column {
  display: flex;
  align-items: flex-start;
  margin-left: -1.5rem;
  margin-right: -1.5rem;
  padding: .575rem 1.5rem;
  &:hover {
    background-color: var(--fts-gray-100);
    cursor: pointer;
  }
  .list-column-checkbox {
    margin-top: .1875rem;
  }
  .list-column-content {
    min-width: 0;
    display: flex;
    .list-column-info {
      min-width: 0;
      display: flex;
      align-items: flex-start;
      line-height: 1.5;
      .list-column-info__icon {
        margin-right: .575rem;
        line-height: inherit;
      }
      .list-column-info__name {
        word-break: break-word;
        overflow-wrap: break-word;
      }
    }
  }
}
.list-column-blank {
  padding: .75rem .75rem 0;
  line-height: 1.75;
  text-align: center;
  color: var(--fts-text);
  .list-column-blank-title {
    font-weight: 500;
  }
  .list-column-blank-description {
    font-size: 85%;
  }
}
</style>